<template>
  <div>
    <h6>
      <span>{{ $t('auction.status.self') }}: </span>
      <b-badge
        v-if="auctionContent.status === 'PAID'"
        variant="success"
      >
        {{ $t('auction.paid') }}
      </b-badge>
      <b-badge
        v-else-if="auctionContent.status === 'NOT_PAID'"
        variant="warning"
      >
        {{ $t('auction.not_paid') }}
      </b-badge>
      <b-badge
        v-else-if="auctionContent.status === 'FINISHED'"
        variant="warning"
      >
        {{ $t('auction.status.FINISHED') }}
      </b-badge>
    </h6>
    <h6>
      {{ $t('auction.winned') }}: {{ winnerTitle }}
    </h6>
    <div class="row">
      <div class="col-8">
        <h6>{{ $t('auction.lot_content') }}</h6>
        <a-collapse class="mb-5">
          <a-collapse-panel
            v-for="promotion in auctionContent.lotPromotions"
            :key="promotion.promotionType.name"
            :header="`${getAuctionTypeTitle(promotion.promotionType.name)} ${promotion.places} ${$t('common.pcs')}`"
          >
            <a-table
              :columns="productColumns"
              :data-source="promotion.promotionPlaces"
              class="product-table"
              :row-key="(record) => record.id"
              :pagination="false"
            >
              <template
                slot="productName"
                slot-scope="text"
              >
                {{ text ? $l(text) : $t('auction.not_provided') }}
              </template>
            </a-table>
          </a-collapse-panel>
          <a-collapse-panel
            :key="'sectionPlaces'"
            :header="$t('auction.lot_types.category')"
          >
            <a-table
              :data-source="auctionContent.sectionPlaces"
              :columns="sectionColumns"
              :row-key="(record) => record.id"
              expand-row-by-click
              :default-expanded-row-keys="defaultExpandedRowKeys"
              :pagination="false"
            >
              <template
                slot="freeSlots"
                slot-scope="_, record"
              >
                {{ record.freeSlots.length }}
              </template>
              <template
                slot="bindSlots"
                slot-scope="_, record"
              >
                {{ record.sectionPlaces.length }}
              </template>
              <template
                slot="expandedRowRender"
                slot-scope="record"
                style="margin: 0"
              >
                <div
                  v-for="place in record.sectionPlaces"
                  :key="place.id"
                  class="d-flex align-items-center"
                >
                  <span class="mr-3">{{ `№${place.position} - ${$l(place.productName)}` }}</span>
                </div>
              </template>
            </a-table>
          </a-collapse-panel>
        </a-collapse>
      </div>
      <div class="col-4">
        <h6>{{ $t('auction.lot_sections') }}</h6>
        <a-tree
          :tree-data="sectionsTree"
        />
      </div>
    </div>

    <a-table
      class="mb-3"
      :columns="bidColumns"
      :data-source="auctionContent.bidList"
      :row-key="'id'"
      :pagination="false"
      bordered
    >
      <template
        slot="date"
        slot-scope="text"
      >
        {{ text | dateFormatter('DD-MM-YY HH:mm') }}
      </template>
      <template
        slot="price"
        slot-scope="text"
      >
        {{ (text) | money }}
      </template>
      <template
        slot="participant"
        slot-scope="name, record"
      >
        <p class="mb-1">
          {{ name }}
        </p>
        <p class="mb-0">
          {{ record.sellerCompany }}
        </p>
      </template>
      <template
        slot="actions"
        slot-scope="record"
      >
        <a-popconfirm
          v-if="auctionContent.status === 'NOT_PAID'"
          :title="$t('common.are_you_sure')"
          :ok-text="$t('common.confirm')"
          :cancel-text="$t('common.cancel')"
          @confirm="() => setWinner(record)"
        >
          <a href="#">{{ $t('auction.set_winner') }}</a>
        </a-popconfirm>
      </template>
    </a-table>
    <div
      v-if="auctionContent.status !== 'FINISHED'"
      class="d-flex justify-content-end"
    >
      <b-button
        v-b-modal.confirm-delete
        variant="outline-danger"
      >
        {{ $t('common.delete') }}
      </b-button>
      <b-modal
        id="confirm-delete"
        centered
        hide-header
        :cancel-title="$t('common.cancel')"
        :ok-title="$t('common.confirm')"
        @ok="handleDeleteAuction"
      >
        {{ $t('auction.confirm_delete') }}
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminView',
  props: {
    categories: {
      type: Array,
      default: () => {},
    },
    settings: {
      type: Object,
      default: () => {},
    },

    auctionContent: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showSelectPromotions: false,
      selectedProducts: [],
      selectedPromotion: null,
      selectionValid: false,
      productColumns: [
        {
          title: this.$t('auction.position'),
          dataIndex: 'position',
          width: 100,
        },
        {
          title: this.$t('product.title'),
          dataIndex: 'productName',
          scopedSlots: {
            customRender: 'productName',
          },
        },
      ],
      sectionColumns: [
        {
          title: this.$t('product.section'),
          dataIndex: 'title',
          scopedSlots: {
            customRender: 'sectionName',
          },
        },
        {
          title: this.$t('auction.count'),
          width: 200,
          scopedSlots: {
            customRender: 'bindSlots',
          },
        },
        {
          title: this.$t('auction.free'),
          width: 100,
          scopedSlots: {
            customRender: 'freeSlots',
          },
        },
      ],
      bidColumns: [
        {
          title: this.$t('auction.bids_log'),
          children: [
            {
              title: this.$t('common.date'),
              dataIndex: 'date_updated',
              scopedSlots: {
                customRender: 'date',
              },
            },
            {
              title: this.$t('auction.participant'),
              dataIndex: 'sellerFullName',
              scopedSlots: {
                customRender: 'participant',
              },
            },
            {
              title: this.$t('auction.bid_price'),
              dataIndex: 'price',
              scopedSlots: {
                customRender: 'price',
              },
            },
            {
              title: '',
              width: 150,
              scopedSlots: {
                customRender: 'actions',
              },
            },
          ],
        },
      ],
    }
  },

  computed: {
    winnerTitle() {
      return (this.auctionContent.bidList[0]
        ? `${this.auctionContent.bidList[0].sellerFullName}, ${this.auctionContent.bidList[0].sellerCompany}`
        : ''
      )
    },

    sectionsTree() {
      const res = []
      this.categories.forEach(category => {
        const subCategories = []
        category.children.forEach(subCat => {
          const sections = subCat.children.filter(section => {
            return this.auctionContent.auctionCategories.includes(Number(section.key.split('_')[1]))
          })

          if (sections.length > 0) {
            subCategories.push({ ...subCat, children: sections })
          }
        })

        if (subCategories.length > 0) {
          res.push({ ...category, children: subCategories })
        }
      })

      return res
    },

    defaultExpandedRowKeys() {
      const keys = []
      this.auctionContent.sectionPlaces.forEach(section => {
        if (section.sectionPlaces.length > 0) {
          keys.push(section.id)
        }
      })

      return keys
    },
  },

  methods: {
    getAuctionTypeTitle(code) {
      return this.$t(`auction.lot_types.${code.toLowerCase()}`)
    },

    setPromotionTypeToSelect(typeName) {
      this.selectedPromotion = this.auctionContent.lotPromotions.find(prom => {
        return prom.promotionType.name === typeName
      })
    },

    handleSelect(isValid, products) {
      this.selectionValid = isValid
      this.selectedProducts = products
    },

    handleSubmit() {
      this.$emit('changePrize', this.selectedProducts)
    },

    handleDeletePosition({ hash_id }) {
      const payload = [
        {
          offerHashId: null,
          variationHashId: null,
          placeHashId: hash_id,
        },
      ]
      this.$emit('changePrize', payload)
    },

    setWinner(record) {
      const payload = {
        auctionId: this.settings.id,
        sellerId: record.seller_id,
        bidHashId: record.hash_id,
      }
      this.$emit('setWinner', payload)
    },

    handleDeleteAuction() {
      this.$emit('delete')
    },
  },
}
</script>
